import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';

import DropLoaderLarge from '@C/Utils/DropLoaderLarge';

import services from './data';
import {
  applyMapping,
  autoDetectMapping,
  generatePassword,
  readSheet,
  readWorkbook,
  splitFullName,
  STAFF_FIELDS,
} from './helpers';
import { ImportSummary } from './ImportSummary';
import { MappingPanel } from './MappingPanel';

const ROLE_OPTIONS = [
  { value: 103, label: 'Cashier' },
  { value: 102, label: 'Manager' },
  { value: 107, label: 'Other Staff' },
];

const SUMMARY_COLUMNS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'password', label: 'Password' },
];

const validateRow = (row) => {
  const missing = [];
  if (!row.first_name) missing.push('First name');
  if (!row.mobile || row.mobile.length !== 10) missing.push('Mobile number');
  return missing;
};

const CashierImport = () => {
  const [role, setRole] = useState(ROLE_OPTIONS[0]);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({});
  const [existingMobiles, setExistingMobiles] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState([]);

  const refreshExistingStaff = async () => {
    try {
      const response = await services.getStaffList();
      const data = response.data?.data || {};
      const all = [
        ...(data.cashiers || []),
        ...(data.managers || []),
        ...(data.other_staff || []),
      ];
      setExistingMobiles(new Set(all.map((item) => item.mobile)));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    refreshExistingStaff();
  }, []);

  const parsedRows = useMemo(
    () =>
      rawRows.map((row) => {
        const mapped = applyMapping(row, STAFF_FIELDS, mapping, defaults);
        const sameColumn =
          mapping.first_name && mapping.first_name === mapping.last_name;
        const combinedName =
          !mapping.last_name && mapped.first_name?.includes(' ');
        if (sameColumn || combinedName) {
          const { first, last } = splitFullName(mapped.first_name);
          mapped.first_name = first;
          mapped.last_name = last;
        }
        return mapped;
      }),
    [rawRows, mapping, defaults],
  );

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults([]);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = readWorkbook(event.target.result);
      const { headers: sheetHeaders, rows } = readSheet(
        workbook,
        workbook.SheetNames[0],
      );
      setHeaders(sheetHeaders);
      setRawRows(rows);
      setMapping(autoDetectMapping(sheetHeaders, STAFF_FIELDS));
      setDefaults({});
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMappingChange = (key, value) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const handleDefaultChange = (key, value) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
  };

  const submitImport = async () => {
    setIsSubmitting(true);

    const seenMobiles = new Set();
    const rowResults = [];
    const toImport = [];

    parsedRows.forEach((row, index) => {
      const rowNumber = index + 2;
      const missing = validateRow(row);
      if (missing.length > 0) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'failed',
          message: `Missing required field(s): ${missing.join(', ')}`,
        });
        return;
      }
      if (existingMobiles.has(row.mobile)) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: 'Staff already exists in Petrometer',
        });
        return;
      }
      if (seenMobiles.has(row.mobile)) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: 'Duplicate mobile number in the uploaded file',
        });
        return;
      }
      seenMobiles.add(row.mobile);
      toImport.push({ row: rowNumber, data: row });
    });

    for (const item of toImport) {
      const { row, data } = item;
      const password = data.password || generatePassword();
      data.password = password;
      try {
        await services.registerStaff({
          username: '',
          password,
          email: data.email || '',
          mobile: data.mobile,
          user_role: role.value,
          first_name: data.first_name,
          last_name: data.last_name || '',
        });
        rowResults.push({ row, data, status: 'added' });
      } catch (error) {
        rowResults.push({
          row,
          data,
          status: 'failed',
          message: error.response?.data?.message || 'Failed to add staff',
        });
      }
    }

    rowResults.sort((a, b) => a.row - b.row);
    setResults(rowResults);
    setIsSubmitting(false);

    const failed = rowResults.filter((item) => item.status === 'failed').length;
    const added = rowResults.filter((item) => item.status === 'added').length;

    if (added > 0) {
      await refreshExistingStaff();
    }

    if (failed === 0) {
      toast.success(`${role.label} import finished successfully`);
      setRawRows([]);
      setHeaders([]);
      setFileName('');
    } else {
      toast.success(
        'Import finished — some rows need attention, see summary below.',
      );
    }
  };

  return (
    <>
      <section className="widget full-width form-table">
        <header>
          <h3>Import Staff</h3>
          <div className="actions">
            <div className="input-wrapper w-130">
              <Select
                options={ROLE_OPTIONS}
                className="select-box-container"
                classNamePrefix="role-selector"
                value={role}
                onChange={(option) => setRole(option || ROLE_OPTIONS[0])}
                isClearable={false}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="input-wrapper file-upload">
              <input
                type="file"
                id="cashier-file"
                accept=".xlsx, .xls"
                onChange={handleFile}
              />
            </div>
          </div>
        </header>
        {fileName && (
          <main>
            <p className="migration-hint">
              {fileName} — {parsedRows.length} row(s) detected. New staff will
              be added with the role: {role.label}.
            </p>
          </main>
        )}
      </section>

      {headers.length > 0 && (
        <>
          <MappingPanel
            fields={STAFF_FIELDS}
            headers={headers}
            mapping={mapping}
            defaults={defaults}
            onChange={handleMappingChange}
            onDefaultChange={handleDefaultChange}
          />
          <div className="form-actions">
            <button
              className={`submit ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
              onClick={submitImport}
            >
              Import {parsedRows.length} {role.label.toLowerCase()}(s)
            </button>
            <span className="migration-hint">
              Rows without a password get a random temporary one — check the
              summary table below to copy it for each new staff member.
            </span>
          </div>
        </>
      )}

      {isSubmitting && <DropLoaderLarge />}

      <ImportSummary
        title={`${role.label} import summary`}
        results={results}
        columns={SUMMARY_COLUMNS}
      />
    </>
  );
};

export { CashierImport };
